import { OrderFormCategoryDTO, OrderFormDTO, OrderFormProductDTO, PartyDTO } from 'support/types/dtos';

export type OrderFormValue = {
  quantity: number;
};

type OrderDataParty = {
  name: string | undefined;
  contact?: OrderDataPartyContact;
} & PartyDTO;

type OrderDataPartyContact = {
  first_name: string | undefined;
  email: string | undefined;
};
export type OrderData = {
  products: Record<string, OrderFormValue>;
  parties?: {
    buyer: OrderDataParty | undefined;
    supplier: OrderDataParty | undefined;
    ship_to: OrderDataParty | undefined;
    bill_to?: OrderDataParty | undefined;
  };
  comments?: string;
  customer_order_number?: string;
};
export const getOrderFormSessionKey = (supplierSlug: string): string => `order-form-${supplierSlug}`;

export const expandOrderableChildren = (
  categories: Array<OrderFormCategoryDTO>,
): { categories: Array<OrderFormCategoryDTO>; childProductIds: Set<string> } => {
  const childProductIds = new Set<string>();

  const expandedCategories = categories.map((category) => ({
    ...category,
    products: category.products.flatMap((product) => {
      const orderableChildren = product.parts?.filter((part) => part.is_orderable) ?? [];
      orderableChildren.forEach((child) => childProductIds.add(child.id));

      return [product, ...orderableChildren];
    }),
  }));

  return { categories: expandedCategories, childProductIds };
};

export const createProductsRecord = (orderForm: OrderFormDTO['data'] | undefined) => {
  if (!orderForm) {
    return {};
  }

  const { categories } = expandOrderableChildren(orderForm.categories);

  return (
    categories
      .flatMap((category) => category.products)
      .reduce<Record<string, OrderFormProductDTO>>((acc, product) => {
        acc[product.id] = product;
        return acc;
      }, {}) ?? {}
  );
};

export function calculateTaxAmountForOrderedProduct(orderedProduct: OrderFormValue, productData: OrderFormProductDTO) {
  const taxRate = productData.tax_rate || 0;
  const subtotal = orderedProduct.quantity * (productData.discounted_unit_price ?? 0);

  return subtotal * (taxRate / 100);
}

export function calculateTotalTax({
  orderedProducts,
  productsData,
}: {
  orderedProducts: Record<string, OrderFormValue>;
  productsData: Record<string, OrderFormProductDTO>;
}): number {
  return Object.entries(orderedProducts).reduce((acc, [productId, ordered]) => {
    const product = productsData[productId];
    if (!product?.tax_rate) {
      return acc;
    }

    const taxAmount = calculateTaxAmountForOrderedProduct(ordered, product);
    acc += taxAmount;

    return acc;
  }, 0);
}

export function isOrderFormWithPrices(
  orderedProducts: Record<string, OrderFormValue>,
  productDataById: Record<string, OrderFormProductDTO>,
): boolean {
  return Object.keys(orderedProducts).some((productId) => {
    const product = productDataById[productId];
    return product && typeof product.discounted_unit_price === 'number';
  });
}
